import { useState } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { formatDateTime, RColumn, RemoteTable } from 'medere-ui-framework';
import {
    DrugTagCostDetailCategory,
    DrugTagCostValuation,
    DrugTagPrescriptionCostDetailDTO,
    DrugTagPrescriptionCostRowDTO,
    DrugTagPrescriptionReportFilter,
} from '../../dto/DrugTagPrescriptionReportDTO';
import { getDrugTagPrescriptionCostDetail } from '../../services/DrugTagPrescriptionReportController';
import { formatMoney } from '../../utils/money';
import TwoLines from './TwoLines';

const integer = new Intl.NumberFormat('es-AR');
/** Unidades de lo preparado: una multidosis usa una parte del envase por dosis (0,033333). */
const units = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 6 });

const CATEGORIES: { value: DrugTagCostDetailCategory; label: string }[] = [
    { value: 'ALL', label: 'Todas' },
    { value: 'APPLIED', label: 'Aplicadas' },
    { value: 'NOT_APPLIED', label: 'No aplicadas preparadas' },
    { value: 'SUSPENDED', label: 'Suspendidas preparadas' },
    { value: 'PENDING', label: 'Pendientes preparadas' },
    { value: 'DISCARDED', label: 'Descartadas' },
];

/** Dosis y costo de la categoría elegida, de los números que ya trae la fila: la lista suma lo mismo. */
function totalsOf(row: DrugTagPrescriptionCostRowDTO, category: DrugTagCostDetailCategory) {
    switch (category) {
        case 'APPLIED': return { doses: row.applied, cost: row.appliedCost };
        case 'NOT_APPLIED': return { doses: row.notAppliedPrepared, cost: row.notAppliedPreparedCost };
        case 'SUSPENDED': return { doses: row.suspendedPrepared, cost: row.suspendedPreparedCost };
        case 'PENDING': return { doses: row.pendingPrepared, cost: row.pendingPreparedCost };
        case 'DISCARDED': return { doses: row.discarded, cost: row.discardedCost };
        default: return { doses: row.preparedTotal, cost: row.totalCost };
    }
}

// Cuerpos de las columnas a nivel de módulo: RemoteTable necesita funciones estables.
type Dose = DrugTagPrescriptionCostDetailDTO;
const dateTime = (value: number | null) => (value ? formatDateTime(value) : '—');
const scheduledBody = (dose: Dose) => <TwoLines main={dateTime(dose.scheduledDate)} detail={dose.patientName} />;
/** El estado actual de la dosis y, debajo, si se aplicó o si esta entrada es una preparación descartada. */
const stateBody = (dose: Dose) => (
    <TwoLines main={dose.applicationStateDescription ?? '—'} detail={stateDetail(dose)} />
);
function stateDetail(dose: Dose) {
    if (dose.category === 'DISCARDED') return 'Preparación descartada';
    return dose.applicationDate ? `Aplicada el ${formatDateTime(dose.applicationDate)}` : null;
}
/** La presentación es la de la fila (está en el título): acá van las unidades de esa presentación. */
const preparedBody = (dose: Dose) => {
    if (dose.units === null) return <TwoLines main="—" detail="Sin preparación registrada" />;
    return <TwoLines main={`${units.format(dose.units)} u.`} detail={dose.multidose ? 'Multidosis' : null} />;
};
/** El total y, debajo, el costo o el precio de cada unidad de la presentación ese día. */
const amountBody = (dose: Dose, withoutValue: string) => (
    <TwoLines main={dose.withoutCost ? withoutValue : formatMoney(dose.cost)}
        detail={dose.unitCost === null ? null : `${formatMoney(dose.unitCost)} c/u`} alignEnd />
);
const costBody = (dose: Dose) => amountBody(dose, 'Sin costo cargado');
const priceBody = (dose: Dose) => amountBody(dose, 'Sin precio de venta');

/**
 * Anchos para el panel de 960 px: 920 de contenido, menos 20 para la barra de scroll vertical, así no aparece una
 * horizontal. La suma es el ancho mínimo de la tabla.
 */
const WIDTHS = { scheduled: 290, state: 210, prepared: 260, cost: 140 };
/** Lo que ocupan arriba de la lista el encabezado, la categoría y el total. */
const ABOVE_LIST_HEIGHT = 300;
const MIN_LIST_HEIGHT = 320;
const TABLE_MIN_WIDTH = WIDTHS.scheduled + WIDTHS.state + WIDTHS.prepared + WIDTHS.cost;
const SCHEDULED_STYLE = { width: WIDTHS.scheduled };
const STATE_STYLE = { width: WIDTHS.state };
const PREPARED_STYLE = { width: WIDTHS.prepared };
const COST_STYLE = { width: WIDTHS.cost };

interface DrugTagCostDetailDrawerProps {
    filter: DrugTagPrescriptionReportFilter;
    row: DrugTagPrescriptionCostRowDTO;
    onHide: () => void;
    /** Error al traer una página: lo muestra la vista. */
    onError: (error: unknown) => void;
}

/**
 * Detalle de una fila de la tab Costo, en un panel lateral: una por una las dosis de esa presentación de ese
 * medicamento en esa obra social, con su estado, las unidades y lo que costaron (o su precio de venta, según la
 * valorización). Se puede ver una categoría sola.
 */
export default function DrugTagCostDetailDrawer({ filter, row, onHide, onError }: DrugTagCostDetailDrawerProps) {
    const [category, setCategory] = useState<DrugTagCostDetailCategory>('ALL');
    const valuation: DrugTagCostValuation = filter.valuation ?? 'COST';
    const byPrice = valuation === 'PRICE';
    const totals = totalsOf(row, category);
    /** La lista llega hasta el pie del panel. */
    const listHeight = Math.max(MIN_LIST_HEIGHT, window.innerHeight - ABOVE_LIST_HEIGHT);

    function loadPage(offset: number, pageSize: number): Promise<Dose[]> {
        return getDrugTagPrescriptionCostDetail(filter, row, category, offset, pageSize).catch((error: unknown) => {
            onError(error);
            return [];
        });
    }

    return (
        <Sidebar
            visible
            position="right"
            onHide={onHide}
            showCloseIcon={false}
            blockScroll
            className="nv-scope nv-drawer drug-tag-cost-drawer"
            aria-labelledby="drug-tag-cost-drawer-title"
        >
            <div className="nv-drawer-layout">
                <header className="nv-drawer-head">
                    <div className="nv-stack-4">
                        <h2 className="nv-modal-title" id="drug-tag-cost-drawer-title">
                            {byPrice ? 'Detalle del precio de venta' : 'Detalle del costo'}
                        </h2>
                        <span className="nv-subtitle">
                            {row.drugName ?? '—'} {row.presentation === null ? '(sin preparación registrada)'
                                : row.presentationName} · {row.healthEntityName ?? '—'}
                        </span>
                    </div>
                    <Button type="button" text className="nv-icon nv-tip" icon="pi pi-times" aria-label="Cerrar"
                        data-pr-tooltip="Cerrar" onClick={onHide} />
                </header>

                <div className="nv-drawer-body">
                    <section className="nv-section" aria-label="Dosis de la fila">
                        <div className="nv-form-row">
                            <div className="nv-field nv-w-m">
                                <label htmlFor="drug-tag-cost-drawer-category">Categoría</label>
                                <Dropdown
                                    inputId="drug-tag-cost-drawer-category"
                                    value={category}
                                    options={CATEGORIES}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setCategory(e.value)}
                                />
                            </div>
                        </div>
                        <span className="nv-muted">
                            {integer.format(totals.doses)} dosis · {formatMoney(totals.cost)}
                        </span>
                        <RemoteTable
                            idTable="drugTagPrescriptionCostDetail"
                            dataKey="prescriptionApplication"
                            getItems={loadPage}
                            params={{ category, valuation }}
                            paramsDelay={0}
                            filterRow={false}
                            notFlex
                            maxHeight={listHeight}
                            tableMinWidth={TABLE_MIN_WIDTH}
                            notFoundText="No hay dosis en esta categoría."
                        >
                            <RColumn field="scheduledDate" header="Programada" style={SCHEDULED_STYLE}
                                body={scheduledBody} />
                            <RColumn field="applicationStateDescription" header="Estado" style={STATE_STYLE}
                                body={stateBody} />
                            <RColumn field="preparedPresentations" header="Preparado" style={PREPARED_STYLE}
                                body={preparedBody} />
                            <RColumn field="cost" header={byPrice ? 'Precio' : 'Costo'} className="text-right"
                                style={COST_STYLE} body={byPrice ? priceBody : costBody} />
                        </RemoteTable>
                    </section>
                </div>
            </div>
        </Sidebar>
    );
}
